import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faXmark } from "@fortawesome/pro-regular-svg-icons";

import { TCustomerTag } from "redux/reducers/models/customer";
import { ICreditBooksFilterValues } from "./CreditBooksFilterDrawer";
import {
  formatBalanceFilterValue,
  formatNamesWithMore,
  hasActiveFilters,
} from "./creditBooksFilterUtils";

import "./creditBooksAppliedFilters.scss";

const MAX_VISIBLE_CUSTOMER_TYPES = 2;
const MAX_VISIBLE_CUSTOMER_TAGS = 1;

interface ICustomerTypeOption {
  id: number;
  title: string;
  full_title?: string;
}

interface IFilterChip {
  key: "balance" | "customer_type" | "customer_tag";
  label: string;
}

interface IProps {
  filters: ICreditBooksFilterValues;
  customerTypes: ICustomerTypeOption[];
  customerTags: TCustomerTag[];
  onRemoveFilter: (key: IFilterChip["key"]) => void;
  onViewMore: () => void;
  onClearAll: () => void;
}

export default function CreditBooksAppliedFilters(props: IProps) {
  const { filters, customerTypes, customerTags, onRemoveFilter, onViewMore, onClearAll } = props;
  const { t } = useTranslation();

  const chips = useMemo(() => {
    const nextChips: IFilterChip[] = [];

    if (filters.balance_min !== "" || filters.balance_max !== "") {
      nextChips.push({
        key: "balance",
        label: `${t("Current balance")}: ${formatBalanceFilterValue(filters.balance_min, filters.balance_max)}`,
      });
    }

    if (filters.customer_type_ids.length > 0) {
      const typeNames = filters.customer_type_ids.map(id => {
        const type = customerTypes.find(customerType => customerType.id === id);
        return type?.full_title ?? type?.title ?? String(id);
      });
      nextChips.push({
        key: "customer_type",
        label: `${t("Customer type")}: ${formatNamesWithMore(typeNames, MAX_VISIBLE_CUSTOMER_TYPES, false)}`,
      });
    }

    if (filters.tag_ids.length > 0) {
      const tagNames = filters.tag_ids.map(id => {
        const tag = customerTags.find(customerTag => customerTag.id === id);
        return tag?.label ?? String(id);
      });
      nextChips.push({
        key: "customer_tag",
        label: `${t("Customer tag")}: ${formatNamesWithMore(tagNames, MAX_VISIBLE_CUSTOMER_TAGS, false)}`,
      });
    }

    return nextChips;
  }, [filters, customerTypes, customerTags, t]);

  if (!hasActiveFilters(filters)) {
    return null;
  }

  return (
    <div className="credit-books-applied-filters">
      <div className="credit-books-applied-filters__chips">
        {chips.map(chip => (
          <div key={chip.key} className="credit-books-applied-filters__chip">
            <span>{chip.label}</span>
            <button
              type="button"
              className="credit-books-applied-filters__chip-remove"
              aria-label={t("Remove filter")}
              onClick={() => onRemoveFilter(chip.key)}
            >
              <FontAwesomeIcon icon={faXmark} />
            </button>
          </div>
        ))}
      </div>
      <div className="credit-books-applied-filters__actions">
        <button type="button" className="credit-books-applied-filters__view-more" onClick={onViewMore}>
          {t("View more")}
        </button>
        <span className="credit-books-applied-filters__actions-divider" aria-hidden="true" />
        <button type="button" className="credit-books-applied-filters__clear-all" onClick={onClearAll}>
          <FontAwesomeIcon icon={faXmark} />
          {t("Clear all")}
        </button>
      </div>
    </div>
  );
}
